import axios from 'axios';

import { toBool } from '../../utils/formatters';
import { mergeActiveRunsForward, normalizeActiveRuns } from '../../utils/testRigHelpers';

export const createDataControls = (deps) => {
  const {
    setLoading,
    setDataLoadError,
    setTestResults,
    setCompletionGraceUntilMs,
    setActiveRuns,
    setIsRunning,
    setCodePromptTemplates,
    setDatasets,
    setSelectedDataset,
    setDatasetSearch,
    setDatasetLoadError,
    setIsLoadingDatasets,
    setIsRefreshingDatasets,
    setIsDatasetDropdownOpen,
    setModelName,
    setResizeMode,
    setCompressionMode,
    setUseEbayRag,
    setUseNotesRag,
    setAssumeHiddenItems,
    setIsChangingModel,
    setIsChangingResizeMode,
    setIsChangingCompressionMode,
    setIsChangingEbayRag,
    setIsChangingNotesRag,
    setIsChangingAssumeHidden,
    fetchSequenceRef,
    latestAppliedFetchSequenceRef,
    hadStatusActiveRunsRef,
    COMPLETION_POLL_GRACE_MS,
    selectedDataset,
    datasets,
  } = deps;

  const applyPromptTemplatesPayload = (promptTemplatesPayload = {}) => {
    const safePayload =
      promptTemplatesPayload && typeof promptTemplatesPayload === 'object'
        ? promptTemplatesPayload
        : {};
    setCodePromptTemplates((previous) => ({
      ebay_off_notes_off:
        typeof safePayload?.ebay_off_notes_off === 'string'
          ? safePayload.ebay_off_notes_off
          : typeof safePayload?.ebay_rag_off === 'string'
            ? safePayload.ebay_rag_off
            : previous.ebay_off_notes_off,
      ebay_on_notes_off:
        typeof safePayload?.ebay_on_notes_off === 'string'
          ? safePayload.ebay_on_notes_off
          : typeof safePayload?.ebay_rag_on === 'string'
            ? safePayload.ebay_rag_on
            : previous.ebay_on_notes_off,
      ebay_off_notes_on:
        typeof safePayload?.ebay_off_notes_on === 'string'
          ? safePayload.ebay_off_notes_on
          : previous.ebay_off_notes_on,
      ebay_on_notes_on:
        typeof safePayload?.ebay_on_notes_on === 'string'
          ? safePayload.ebay_on_notes_on
          : previous.ebay_on_notes_on,
    }));
  };

  const fetchPromptTemplates = async () => {
    try {
      const statusRes = await axios.get('/test_status', {
        params: { include_prompt_templates: true },
      });
      const statusPayload = statusRes?.data || {};
      applyPromptTemplatesPayload(statusPayload?.prompt_templates);
    } catch (error) {
      console.warn('Failed to load prompt templates:', error);
    }
  };

  const fetchData = async ({ showLoading = true } = {}) => {
    const requestSequence = ++fetchSequenceRef.current;
    let shouldApply = true;
    let loadingClearedEarly = false;

    if (showLoading) setLoading(true);
    setDataLoadError('');

    try {
      const statusTask = axios
        .get('/test_status', { params: { include_prompt_templates: false } })
        .then(
          (response) => ({ ok: true, response }),
          (error) => ({ ok: false, error }),
        );
      const resultsRes = await axios.get('/get_test_results', {
        // `identification` makes the backend score each run for the Brand %
        // and Name F1 columns. It still returns summaries — the per-item
        // answers are loaded to score and dropped before the response.
        params: { summary: true, identification: true },
      });
      const nextRuns = Array.isArray(resultsRes?.data) ? resultsRes.data : [];

      if (requestSequence < latestAppliedFetchSequenceRef.current) {
        shouldApply = false;
      } else {
        latestAppliedFetchSequenceRef.current = requestSequence;
        setTestResults((previousRuns) => {
          const previousByRunId = new Map(
            (Array.isArray(previousRuns) ? previousRuns : [])
              .map((run) => [String(run?.run_id || '').trim(), run])
              .filter(([runId]) => Boolean(runId)),
          );

          return nextRuns.map((run) => {
            const runId = String(run?.run_id || '').trim();
            if (!runId) return run;
            const previousRun = previousByRunId.get(runId);
            if (!previousRun) return run;

            const mergedRun = { ...run };
            if (
              Array.isArray(previousRun?.results) &&
              !Array.isArray(run?.results)
            ) {
              mergedRun.results = previousRun.results;
            }
            if (
              (typeof run?.prompt_template !== 'string' ||
                !run.prompt_template.trim()) &&
              typeof previousRun?.prompt_template === 'string' &&
              previousRun.prompt_template.trim()
            ) {
              mergedRun.prompt_template = previousRun.prompt_template;
            }
            if (
              !Array.isArray(run?.skipped_lots) &&
              Array.isArray(previousRun?.skipped_lots)
            ) {
              mergedRun.skipped_lots = previousRun.skipped_lots;
            }
            if (
              !Array.isArray(run?.skipped_details) &&
              Array.isArray(previousRun?.skipped_details)
            ) {
              mergedRun.skipped_details = previousRun.skipped_details;
            }
            return mergedRun;
          });
        });
        if (showLoading) {
          setLoading(false);
          loadingClearedEarly = true;
        }
      }

      const statusResult = await statusTask;
      if (!statusResult.ok) {
        console.warn('Error fetching test status:', statusResult.error);
      } else {
        const statusPayload = statusResult?.response?.data || {};
        const rawActiveRuns = Array.isArray(statusPayload?.active_runs)
          ? statusPayload.active_runs
          : Array.isArray(statusPayload?.run_contexts)
            ? statusPayload.run_contexts
            : statusPayload?.run_context &&
                typeof statusPayload.run_context === 'object'
              ? [statusPayload.run_context]
              : [];
        const normalizedActiveRuns = normalizeActiveRuns(rawActiveRuns);
        const nextIsRunning =
          statusPayload.is_running === true || normalizedActiveRuns.length > 0;

        if (requestSequence < latestAppliedFetchSequenceRef.current) {
          shouldApply = false;
        } else {
          latestAppliedFetchSequenceRef.current = requestSequence;
          const statusHasAnyActiveRuns = normalizedActiveRuns.length > 0;
          if (!statusHasAnyActiveRuns && hadStatusActiveRunsRef.current) {
            setCompletionGraceUntilMs(Date.now() + COMPLETION_POLL_GRACE_MS);
          } else if (statusHasAnyActiveRuns) {
            setCompletionGraceUntilMs(0);
          }
          hadStatusActiveRunsRef.current = statusHasAnyActiveRuns;
          setActiveRuns((previous) =>
            mergeActiveRunsForward(previous, normalizedActiveRuns),
          );
          setIsRunning(nextIsRunning || normalizedActiveRuns.length > 0);
        }
      }
    } catch (error) {
      if (requestSequence < latestAppliedFetchSequenceRef.current) {
        shouldApply = false;
      } else {
        latestAppliedFetchSequenceRef.current = requestSequence;
        console.error('Error fetching test rig data:', error);
        setDataLoadError(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to load test rig data.',
        );
      }
    } finally {
      if (showLoading && shouldApply && !loadingClearedEarly) {
        setLoading(false);
      }
    }
  };

  const refreshDatasets = async () => {
    if (deps.isRefreshingDatasets) return;
    setIsRefreshingDatasets(true);
    setDatasetLoadError('');
    try {
      const response = await axios.post('/refresh_test_datasets');
      const nextDatasets = Array.isArray(response?.data?.datasets)
        ? response.data.datasets
        : [];
      setDatasets(nextDatasets);
      if (nextDatasets.length === 0) {
        setSelectedDataset(null);
        setDatasetSearch('');
        return;
      }
      const currentPrefix = selectedDataset?.prefix || '';
      const matchingSelection =
        nextDatasets.find((entry) => (entry.prefix || '') === currentPrefix) ||
        null;
      if (matchingSelection) {
        setSelectedDataset(matchingSelection);
        setDatasetSearch(
          matchingSelection.name || matchingSelection.prefix || '',
        );
      } else {
        setSelectedDataset(null);
        setDatasetSearch('');
      }
    } catch (error) {
      console.error('Error refreshing test datasets:', error);
      setDatasetLoadError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to refresh datasets.',
      );
    } finally {
      setIsRefreshingDatasets(false);
    }
  };

  const fetchDatasets = async () => {
    setIsLoadingDatasets(true);
    setDatasetLoadError('');
    try {
      const response = await axios.get('/test_datasets');
      const nextDatasets = Array.isArray(response?.data?.datasets)
        ? response.data.datasets
        : [];
      setDatasets(nextDatasets);

      if (nextDatasets.length === 0) {
        setSelectedDataset(null);
        setDatasetSearch('');
        return;
      }

      const currentPrefix = selectedDataset?.prefix || '';
      const matchingSelection =
        nextDatasets.find((entry) => (entry.prefix || '') === currentPrefix) ||
        null;
      if (matchingSelection) {
        setSelectedDataset(matchingSelection);
        setDatasetSearch(
          matchingSelection.name || matchingSelection.prefix || '',
        );
        return;
      }

      setSelectedDataset(null);
      setDatasetSearch('');
    } catch (error) {
      console.error('Error loading test datasets:', error);
      setDatasets([]);
      setSelectedDataset(null);
      setDatasetSearch('');
      setDatasetLoadError(
        error?.response?.data?.message || error?.message || 'Failed to load datasets.',
      );
    } finally {
      setIsLoadingDatasets(false);
    }
  };

  const selectDataset = (dataset) => {
    if (!dataset) return;
    setSelectedDataset(dataset);
    setDatasetSearch(dataset.name || dataset.prefix || '');
    setIsDatasetDropdownOpen(false);
  };

  const handleModelChange = (newModel) => {
    setIsChangingModel(true);
    setModelName(newModel);
    setIsChangingModel(false);
  };

  const handleResizeModeChange = (newResizeMode) => {
    setIsChangingResizeMode(true);
    setResizeMode(newResizeMode);
    setIsChangingResizeMode(false);
  };

  const handleCompressionModeChange = (newCompressionMode) => {
    setIsChangingCompressionMode(true);
    setCompressionMode(newCompressionMode);
    setIsChangingCompressionMode(false);
  };

  const handleUseEbayRagChange = (nextValue) => {
    setIsChangingEbayRag(true);
    setUseEbayRag(toBool(nextValue, false));
    setIsChangingEbayRag(false);
  };

  const handleUseNotesRagChange = (nextValue) => {
    setIsChangingNotesRag(true);
    setUseNotesRag(toBool(nextValue, false));
    setIsChangingNotesRag(false);
  };

  const handleAssumeHiddenItemsChange = (nextValue) => {
    setIsChangingAssumeHidden(true);
    setAssumeHiddenItems(toBool(nextValue, true));
    setIsChangingAssumeHidden(false);
  };

  const normalizedDatasetSearch = deps.datasetSearch.trim().toLowerCase();
  const filteredDatasets = datasets.filter((dataset) => {
    if (!normalizedDatasetSearch) return true;
    const name = (dataset?.name || '').toLowerCase();
    const prefix = (dataset?.prefix || '').toLowerCase();
    return (
      name.includes(normalizedDatasetSearch) ||
      prefix.includes(normalizedDatasetSearch)
    );
  });

  return {
    applyPromptTemplatesPayload,
    fetchPromptTemplates,
    fetchData,
    refreshDatasets,
    fetchDatasets,
    selectDataset,
    handleModelChange,
    handleResizeModeChange,
    handleCompressionModeChange,
    handleUseEbayRagChange,
    handleUseNotesRagChange,
    handleAssumeHiddenItemsChange,
    filteredDatasets,
  };
};
